import React, { useRef, useState } from 'react';
import { findSoundsInString } from '@/utils/sounds';
import { PREFIXES, SUFFIXES } from '@/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia, RoundPlayButton } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { GrabGameBuildWords } from '../components/GrabGameBuildWords';

interface ReadingManipulatingPrefixesAndSuffixesStepProps
  extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
  sounds: Array<string>;
  wordChoices: Array<string>;
}

const ReadingManipulatingPrefixesAndSuffixesStep = ({
  activity,
  mode,
  isPreview,
  wrapperClassName,
  index,
  total,
  word,
  sounds,
  wordChoices,
}: ReadingManipulatingPrefixesAndSuffixesStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <ActivityStep
      stepIdentifier={`${activity.id}-0`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      showTitle={true}
      showActivityPagination={false}
    >
      <RoundPlayButton
        color="white"
        className="my-6"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        size="sm"
        disabled={!playerReady}
        pressHint={!playedOnce}
      />

      <WhitePanel className="my-6" mode="letter">
        <CdnMedia
          className="block mx-auto w-auto h-full"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />
      </WhitePanel>

      <GrabGameBuildWords word={word} parts1={sounds} parts2={wordChoices} />

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};
export const ReadingManipulatingPrefixesAndSuffixes = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  if (!activity.words2) {
    throw new Error('Words 2 are required for this activity');
  }

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { sounds: soundsStr, words, words2 } = activity;

  const sounds = findSoundsInString([...PREFIXES, ...SUFFIXES], soundsStr);

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={words2.length + 1}
        />,
        ...words2.map((word, index) => (
          <ReadingManipulatingPrefixesAndSuffixesStep
            key={`${activity.id}-${index}`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={words2.length + 1}
            word={word}
            sounds={sounds}
            wordChoices={words || []}
          />
        )),
      ]}
    />
  );
};
